import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Check, Copy, Mail, Pencil, Send, X } from 'lucide-react'

interface EmailComposerCardProps {
  messageId: string
  content: string
  email?: {
    intro?: string
    subject: string
    body: string
  }
}

const EMAIL_SUBJECT_RE = /(?:^|\n)\s*(?:Objet|Subject)\s*[\u00a0:]\s*(.+)/i

export const parseEmailContent = (content: string) => {
  const normalized = content.replace(/\r\n/g, '\n').trim()
  const match = normalized.match(EMAIL_SUBJECT_RE)
  if (!match) return null
  const subject = match[1].replace(/^\*+|\*+$/g, '').trim()
  const body = normalized.slice((match.index || 0) + match[0].length).replace(/^\s*[\n:-]?\s*/, '').trim()
  if (!subject || body.length < 20) return null
  return { subject, body }
}

export const EmailComposerCard: React.FC<EmailComposerCardProps> = ({ messageId, content, email }) => {
  const parsed = useMemo(() => email || parseEmailContent(content), [content, email])
  const storageKey = `selektia:email-draft:${messageId}`
  const [subject, setSubject] = useState(parsed?.subject || '')
  const [body, setBody] = useState(parsed?.body || '')
  const [editing, setEditing] = useState(false)
  const [copied, setCopied] = useState(false)
  const [shareOpen, setShareOpen] = useState(false)
  const shareMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!shareOpen) return
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!shareMenuRef.current?.contains(event.target as Node)) setShareOpen(false)
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setShareOpen(false)
    }
    document.addEventListener('mousedown', closeOnOutsideClick)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [shareOpen])

  useEffect(() => {
    if (!parsed) return
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || 'null')
      setSubject(saved?.subject || parsed.subject)
      setBody(saved?.body || parsed.body)
    } catch {
      setSubject(parsed.subject)
      setBody(parsed.body)
    }
  }, [parsed, storageKey])

  useEffect(() => {
    if (!subject && !body) return
    localStorage.setItem(storageKey, JSON.stringify({ subject, body, updatedAt: Date.now() }))
  }, [subject, body, storageKey])

  if (!parsed) return null
  const plainText = `Objet : ${subject}\n\n${body}`
  const openCompose = (provider: 'gmail' | 'outlook') => {
    const encodedSubject = encodeURIComponent(subject)
    const encodedBody = encodeURIComponent(body)
    const url = provider === 'gmail'
      ? `https://mail.google.com/mail/?view=cm&fs=1&su=${encodedSubject}&body=${encodedBody}`
      : `https://outlook.live.com/mail/0/deeplink/compose?subject=${encodedSubject}&body=${encodedBody}`
    window.open(url, '_blank', 'noopener,noreferrer')
    setShareOpen(false)
  }

  const copy = async () => {
    await navigator.clipboard.writeText(plainText)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1800)
  }

  return (
    <div className="w-full max-w-3xl space-y-3">
      {email?.intro && <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">{email.intro}</p>}
      <div className="overflow-visible rounded-2xl border border-slate-200 bg-white text-slate-800 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-700">
        <button type="button" onClick={() => setEditing((value) => !value)} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium transition hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800" title={editing ? 'Terminer les modifications' : 'Modifier'}>
          {editing ? <X className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
          {editing ? 'Terminer' : 'Modifier'}
        </button>
        <div className="flex items-center gap-1">
          <button type="button" onClick={copy} className="inline-flex h-8 w-8 items-center justify-center rounded-lg transition hover:bg-slate-100 dark:hover:bg-slate-800" aria-label="Copier" title="Copier">
            {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
          </button>
          <div ref={shareMenuRef} className="relative">
            <button type="button" onClick={() => setShareOpen((value) => !value)} className="inline-flex h-8 w-8 items-center justify-center rounded-lg transition hover:bg-slate-100 dark:hover:bg-slate-800" aria-label="Partager" title="Partager" aria-expanded={shareOpen}>
              <Send className="h-4 w-4" />
            </button>
            {shareOpen && (
              <div className="absolute right-0 top-10 z-20 w-44 rounded-xl border border-slate-200 bg-white p-1.5 text-slate-800 shadow-xl dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
                <button type="button" onClick={() => openCompose('gmail')} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-800"><Mail className="h-4 w-4 text-red-500" /> Gmail</button>
                <button type="button" onClick={() => openCompose('outlook')} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-800"><Mail className="h-4 w-4 text-blue-500" /> Outlook</button>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="space-y-4 px-5 py-4 sm:px-7 sm:py-5">
        {editing ? (
          <>
            <input value={subject} onChange={(event) => setSubject(event.target.value)} className="w-full border-b border-slate-200 bg-transparent pb-3 text-lg font-semibold outline-none focus:border-teal-400 dark:border-slate-700" aria-label="Objet" />
            <textarea value={body} onChange={(event) => setBody(event.target.value)} className="min-h-[280px] w-full resize-y bg-transparent text-[15px] leading-7 outline-none focus:ring-0" aria-label="Corps du message" />
          </>
        ) : (
          <>
            <h3 className="border-b border-slate-200 pb-3 text-lg font-semibold dark:border-slate-700">{subject}</h3>
            <div className="whitespace-pre-wrap text-[15px] leading-7 text-slate-700 dark:text-slate-200">{body}</div>
          </>
        )}
        <p className="text-xs text-slate-400">Enregistré automatiquement</p>
      </div>
      </div>
    </div>
  )
}
